// Central keymap: single source of truth for shortcuts + the discoverable panel.
// Matching uses KeyboardEvent.code (physical key) so Alt/Option combos work on
// macOS, where Option+letter would otherwise produce a special character in .key.

export interface ShortcutDef {
  action: string;
  combo: string; // canonical match string
  keys: string[]; // display tokens
  desc: string;
  section: string;
}

export const SHORTCUTS: ShortcutDef[] = [
  // Navigation
  { action: "zoomIn", combo: "+", keys: ["+"], desc: "Zoom in", section: "Navigation" },
  { action: "zoomOut", combo: "-", keys: ["−"], desc: "Zoom out", section: "Navigation" },
  { action: "panLeft", combo: "ArrowLeft", keys: ["←"], desc: "Pan left", section: "Navigation" },
  { action: "panRight", combo: "ArrowRight", keys: ["→"], desc: "Pan right", section: "Navigation" },
  { action: "priceUp", combo: "ArrowUp", keys: ["↑"], desc: "Move price up", section: "Navigation" },
  { action: "priceDown", combo: "ArrowDown", keys: ["↓"], desc: "Move price down", section: "Navigation" },
  { action: "resetChart", combo: "Alt+r", keys: ["⌥", "R"], desc: "Reset chart", section: "Navigation" },
  { action: "fitData", combo: "Shift+r", keys: ["⇧", "R"], desc: "Fit chart to data", section: "Navigation" },
  { action: "goToDate", combo: "g", keys: ["G"], desc: "Go to date…", section: "Navigation" },
  { action: "toggleCrosshair", combo: "c", keys: ["C"], desc: "Toggle crosshair", section: "Navigation" },
  { action: "toggleVolume", combo: "Shift+v", keys: ["⇧", "V"], desc: "Toggle volume", section: "Navigation" },
  { action: "fullscreen", combo: "Shift+f", keys: ["⇧", "F"], desc: "Toggle fullscreen", section: "Navigation" },
  { action: "shortcuts", combo: "?", keys: ["?"], desc: "Show shortcuts", section: "Navigation" },

  // Timeframes
  { action: "tfPrev", combo: ",", keys: [","], desc: "Previous timeframe", section: "Timeframes" },
  { action: "tfNext", combo: ".", keys: ["."], desc: "Next timeframe", section: "Timeframes" },
  { action: "tf1", combo: "1", keys: ["1", "–", "9"], desc: "Timeframe 1–9 by slot", section: "Timeframes" },

  // Drawings
  { action: "undo", combo: "Mod+z", keys: ["⌘", "Z"], desc: "Undo", section: "Drawings" },
  { action: "redo", combo: "Mod+Shift+z", keys: ["⌘", "⇧", "Z"], desc: "Redo", section: "Drawings" },
  { action: "deleteSel", combo: "Delete", keys: ["⌫"], desc: "Delete selected", section: "Drawings" },
  { action: "deleteAll", combo: "Alt+Shift+Backspace", keys: ["⌥", "⇧", "⌫"], desc: "Delete all drawings", section: "Drawings" },
  { action: "cursor", combo: "Escape", keys: ["Esc"], desc: "Cursor / cancel", section: "Drawings" },

  // Tools
  { action: "tool:position", combo: "Alt+f", keys: ["⌥", "F"], desc: "Position (1:3, auto long/short)", section: "Tools" },
  { action: "tool:trendline", combo: "Alt+t", keys: ["⌥", "T"], desc: "Trend line", section: "Tools" },
  { action: "tool:hline", combo: "Alt+h", keys: ["⌥", "H"], desc: "Horizontal line", section: "Tools" },
  { action: "tool:vline", combo: "Alt+v", keys: ["⌥", "V"], desc: "Vertical line", section: "Tools" },
  { action: "tool:ray", combo: "Alt+y", keys: ["⌥", "Y"], desc: "Ray", section: "Tools" },
  { action: "tool:extended", combo: "Alt+e", keys: ["⌥", "E"], desc: "Extended line", section: "Tools" },
  { action: "tool:arrow", combo: "Alt+a", keys: ["⌥", "A"], desc: "Arrow", section: "Tools" },
  { action: "tool:rect", combo: "Alt+b", keys: ["⌥", "B"], desc: "Rectangle", section: "Tools" },
  { action: "tool:ellipse", combo: "Alt+o", keys: ["⌥", "O"], desc: "Ellipse", section: "Tools" },
  { action: "tool:channel", combo: "Alt+p", keys: ["⌥", "P"], desc: "Parallel channel", section: "Tools" },
  { action: "tool:fibretr", combo: "Alt+Shift+f", keys: ["⌥", "⇧", "F"], desc: "Fib retracement", section: "Tools" },
  { action: "tool:fibext", combo: "Alt+x", keys: ["⌥", "X"], desc: "Fib extension", section: "Tools" },
  { action: "tool:pricerange", combo: "Alt+m", keys: ["⌥", "M"], desc: "Price measurement", section: "Tools" },
  { action: "tool:daterange", combo: "Alt+d", keys: ["⌥", "D"], desc: "Date/time measurement", section: "Tools" },
  { action: "tool:riskreward", combo: "Alt+k", keys: ["⌥", "K"], desc: "Risk/reward", section: "Tools" },
];

export function comboFromEvent(e: KeyboardEvent): string {
  const parts: string[] = [];
  if (e.metaKey || e.ctrlKey) parts.push("Mod");
  if (e.altKey) parts.push("Alt");
  const shift = e.shiftKey;
  const code = e.code;
  let key = "";

  if (/^Key[A-Z]$/.test(code)) key = code.slice(3).toLowerCase();
  else if (/^Digit[0-9]$/.test(code)) key = code.slice(5);
  else if (/^Numpad[0-9]$/.test(code)) key = code.slice(6);
  else if (code === "Equal" || code === "NumpadAdd") key = "+";
  else if (code === "Minus" || code === "NumpadSubtract") key = "-";
  else if (code === "Slash") {
    if (shift) return [...parts, "?"].join("+");
    key = "/";
  } else if (code === "Comma") key = ",";
  else if (code === "Period") key = ".";
  else if (code === "ArrowLeft") key = "ArrowLeft";
  else if (code === "ArrowRight") key = "ArrowRight";
  else if (code === "ArrowUp") key = "ArrowUp";
  else if (code === "ArrowDown") key = "ArrowDown";
  else if (code === "Delete") key = "Delete";
  else if (code === "Backspace") key = "Backspace";
  else if (code === "Escape") key = "Escape";
  else if (code === "Space") key = "Space";
  else key = e.key.length === 1 ? e.key.toLowerCase() : e.key;

  // '+' and '-' shouldn't carry the Shift used to type them
  if (shift && key !== "+" && key !== "-") parts.push("Shift");
  parts.push(key);
  return parts.join("+");
}

export function createShortcutHandler(actions: Record<string, (arg?: any) => void>) {
  const map = new Map<string, string>();
  for (const s of SHORTCUTS) map.set(s.combo, s.action);
  // aliases: macOS "delete" key reports as Backspace
  if (!map.has("Backspace")) map.set("Backspace", "deleteSel");

  return (e: KeyboardEvent) => {
    const target = e.target as HTMLElement;
    if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) return;
    const combo = comboFromEvent(e);
    if (/^[1-9]$/.test(combo) && actions["tfSlot"]) {
      actions["tfSlot"](parseInt(combo, 10) - 1);
      e.preventDefault();
      return;
    }
    const action = map.get(combo);
    if (action && actions[action]) {
      actions[action]();
      e.preventDefault();
    }
  };
}

export const SECTIONS = ["Navigation", "Timeframes", "Drawings", "Tools"];
